import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { colors } from 'lib/colors';
import { FaRegTrashAlt } from 'react-icons/fa';
import { RiCheckboxCircleFill } from 'react-icons/ri';
import {
  type FILE_UPLOAD_STATUS,
  formatFileSize,
} from 'lib/fileUploadConstants';
import type { FileUploadItem } from 'hooks/useFileUpload';
import FileIconContainer from './FileIconContainer';

interface BaseFileItemProps {
  className?: string;
  children?: React.ReactNode;
}

const BaseFileItem: React.FC<BaseFileItemProps> = ({ className, children }) => (
  <div
    className={clsx(
      'flex flex-1 flex-row items-center justify-between border-gray-100 rounded-md border px-4 py-3 transition-all duration-200',
      className
    )}
  >
    {children}
  </div>
);

interface FileIconContainerWrapperProps {
  fileName: string;
  status: (typeof FILE_UPLOAD_STATUS)[keyof typeof FILE_UPLOAD_STATUS];
}

const FileIconContainerWrapper: React.FC<FileIconContainerWrapperProps> = ({
  fileName,
  status,
}) => {
  const getFileIconColor = (): string => {
    switch (status) {
      case 'ERROR':
        return colors.error;
      default:
        return '#6b7280';
    }
  };

  return <FileIconContainer fileName={fileName} color={getFileIconColor()} />;
};

interface FileInfoProps {
  fileName: string;
  fileSize: number;
  status: (typeof FILE_UPLOAD_STATUS)[keyof typeof FILE_UPLOAD_STATUS];
  hasError?: boolean;
  handleRemove?: () => void;
}

const FileInfo: React.FC<FileInfoProps> = ({
  fileName,
  fileSize,
  status,
  hasError,
  handleRemove,
}) => {
  const getStatusText = (): string => {
    if (hasError) return 'Upload failed, please try again';
    return fileName;
  };

  return (
    <div className="flex flex-col flex-1">
      <div className="flex items-center justify-between">
        <span
          className={clsx(
            'text-title text-sm font-bold truncate',
            hasError ? 'text-error' : 'text-gray-60'
          )}
        >
          {getStatusText()}
        </span>
        {status === 'SUCCESS' && (
          <RiCheckboxCircleFill
            size={20}
            className="text-tertiary-dark ml-2 flex-shrink-0"
          />
        )}
        {hasError && handleRemove && (
          <FaRegTrashAlt
            onClick={handleRemove}
            size={16}
            className="text-error ml-2 flex-shrink-0 cursor-pointer"
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <span
          className={clsx(
            'text-xs text-body-secondary',
            hasError && 'text-error'
          )}
        >
          {hasError ? fileName : formatFileSize(fileSize)}
        </span>
      </div>
    </div>
  );
};

interface ProgressBarProps {
  progress: number;
  status: (typeof FILE_UPLOAD_STATUS)[keyof typeof FILE_UPLOAD_STATUS];
}

const ProgressBar: React.FC<ProgressBarProps> = ({ progress, status }) => {
  // Hide progress bar only on error, show green progress bar on success
  if (status === 'ERROR') {
    return null;
  }

  const getProgressColor = (): string => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-tertiary-dark';
      case 'UPLOADING':
        return 'bg-primary';
      case 'PAUSED':
        return 'bg-gray-400';
      case 'CANCELLED':
        return 'bg-gray-400';
      default:
        return 'bg-gray-200';
    }
  };

  const getProgressText = (): string => {
    return `${progress}%`;
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      <div className="w-full bg-gray-200 rounded h-2">
        <div
          className={clsx(
            'h-2 rounded transition-all duration-300',
            getProgressColor()
          )}
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="min-w-[35px] text-xs text-body-secondary">
        {getProgressText()}
      </span>
    </div>
  );
};

interface UploadFileItemProps {
  uploadItem: FileUploadItem;
  onRetry?: (fileKey: string) => void;
  onRemove?: (fileKey: string) => void;
  showActions?: boolean;
}

export const UploadFileItem: React.FC<UploadFileItemProps> = ({
  uploadItem,
  onRetry,
  onRemove,
  showActions = true,
}) => {
  const { file, key, progress, status, error } = uploadItem;
  const hasError = status === 'ERROR' && !!error;
  const [shouldStartFade, setShouldStartFade] = useState(false);

  const handleRetry = (): void => {
    onRetry?.(key);
  };

  const handleRemove = (): void => {
    onRemove?.(key);
  };

  const getContainerStyle = (): string => {
    return clsx(
      'border-gray-100',
      shouldStartFade && 'opacity-0 transform scale-95 !mb-0',
      !shouldStartFade && 'mb-2',
      'transition-all duration-500 ease-in-out'
    );
  };

  const getWrapperStyle = (): React.CSSProperties => {
    if (shouldStartFade) {
      return {
        maxHeight: '0px',
        overflow: 'hidden',
        transition: 'all 0.5s ease-in-out',
      };
    }
    return {
      maxHeight: '150px',
      transition: 'all 0.5s ease-in-out',
    };
  };

  useEffect(() => {
    if (status === 'SUCCESS') {
      const fadeTimer = setTimeout(() => {
        setShouldStartFade(true);
      }, 2500);

      const removeTimer = setTimeout(() => {
        handleRemove();
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [status, handleRemove]);

  return (
    <div style={getWrapperStyle()}>
      <BaseFileItem className={getContainerStyle()}>
        <div className="flex flex-col flex-1 w-100">
          <div className="flex items-start gap-2">
            <FileIconContainerWrapper fileName={file.name} status={status} />
            <FileInfo
              fileName={file.name}
              fileSize={file.size}
              status={status}
              hasError={hasError}
              handleRemove={handleRemove}
            />
          </div>
          {!hasError && <ProgressBar progress={progress} status={status} />}

          {hasError && (
            <div className="mt-2">
              <button
                className="pl-[28px] text-error cursor-pointer hover:no-underline font-bold"
                onClick={handleRetry}
                type="button"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </BaseFileItem>
    </div>
  );
};
